import { useTranslation } from "react-i18next";
import { useRef, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { PencilLine, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { setPendingFile } from "@/lib/pending-file";
import { cn } from "@/lib/utils";
import { TextToOrderDialog } from "@/components/orders/TextToOrderDialog";

const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",
  "image/tiff",
  "image/tif",
  "image/heic",
  "image/heif",
  "application/pdf",
];

const HEIC_EXTENSIONS = [".heic", ".heif"];

const ACCEPT_STRING = "image/*,.pdf";

interface PhotoToOrderPanelProps {
  disabled?: boolean;
}

export function PhotoToOrderPanel({ disabled }: PhotoToOrderPanelProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [textDialogOpen, setTextDialogOpen] = useState(false);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const nameLower = file.name.toLowerCase();
      const isHeic = HEIC_EXTENSIONS.some((ext) => nameLower.endsWith(ext));
      if (!ACCEPTED_FILE_TYPES.includes(file.type) && !isHeic) return;

      setPendingFile(file);
      setLocation("/list");
    },
    [setLocation],
  );

  return (
    <div
      className={cn(
        "flex items-center gap-2.5 md:flex-col md:items-center md:gap-3 md:py-1",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 md:w-full"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
      >
        <Upload className="h-4 w-4" aria-hidden="true" />
        {t("components.orders.photoToOrderPanel.photoOrFile")}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 md:w-full"
        onClick={() => setTextDialogOpen(true)}
        disabled={disabled}
      >
        <PencilLine className="h-4 w-4" aria-hidden="true" />
        {t("components.orders.photoToOrderPanel.enterText")}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_STRING}
        className="hidden"
        onChange={handleFileChange}
        aria-label={t("components.orders.photoToOrderPanel.uploadImageOrPdf")}
      />
      <TextToOrderDialog open={textDialogOpen} onOpenChange={setTextDialogOpen} />
    </div>
  );
}
